import { BufferGeometry, Material, Mesh, Object3D, Scene } from "three";

/** Materyal–geometri önbelleğinin ne zaman yeniden taranacağı; birleştirilebilir. */
export enum MaterialGeometryInvalidationMode {
  None = 0,
  /** `invalidate()` / `markStale()` sonrası bir sonraki `sync` tam yenileme yapar. */
  Explicit = 1,
  /** Yapı imzası: mesh / geometry.uuid / material.uuid / visible değişince yenileme. */
  StructureSignature = 2,
}

function isMeshLike(obj: Object3D): obj is Mesh {
  const m = obj as Mesh;
  return m.isMesh === true;
}

function normalizeMaterials(material: Material | Material[]): Material[] {
  return Array.isArray(material) ? material : [material];
}

function pushGeometryForMaterial(
  buckets: Map<Material, Set<BufferGeometry>>,
  material: Material | undefined,
  geometry: BufferGeometry,
) {
  if (!material) return;
  let set = buckets.get(material);
  if (!set) {
    set = new Set();
    buckets.set(material, set);
  }
  set.add(geometry);
}

/** Tek geçişte yapı imzası parçaları + bucket’lar. */
function collectStructureAndBuckets(scene: Scene): {
  signature: string;
  buckets: Map<Material, BufferGeometry[]>;
} {
  const parts: string[] = [];
  const raw = new Map<Material, Set<BufferGeometry>>();

  scene.traverse((obj) => {
    if (!isMeshLike(obj)) return;
    const mesh = obj as Mesh;
    const geom = mesh.geometry as BufferGeometry | undefined;
    if (!geom) return;

    const mats = normalizeMaterials(mesh.material);
    const matUuids = mats.map((m) => m?.uuid ?? "").join(",");

    parts.push(`${mesh.id}:${geom.uuid}:${matUuids}:${mesh.visible}`);

    if (mats.length === 1) {
      pushGeometryForMaterial(raw, mats[0], geom);
    } else {
      const groups = (mesh as Mesh & { groups?: { materialIndex?: number }[] })
        .groups;
      if (groups && groups.length > 0) {
        for (const g of groups) {
          const mat = mats[g.materialIndex ?? 0];
          pushGeometryForMaterial(raw, mat, geom);
        }
      } else {
        for (const mat of mats) {
          pushGeometryForMaterial(raw, mat, geom);
        }
      }
    }
  });

  parts.sort();
  const signature = parts.join("|");

  const buckets = new Map<Material, BufferGeometry[]>();
  for (const [mat, set] of raw) {
    buckets.set(mat, Array.from(set));
  }

  return { signature, buckets };
}

function collectStructureSignatureOnly(scene: Scene): string {
  const parts: string[] = [];
  scene.traverse((obj) => {
    if (!isMeshLike(obj)) return;
    const mesh = obj as Mesh;
    const geom = mesh.geometry as BufferGeometry | undefined;
    if (!geom) return;
    const mats = normalizeMaterials(mesh.material);
    const matUuids = mats.map((m) => m?.uuid ?? "").join(",");
    parts.push(`${mesh.id}:${geom.uuid}:${matUuids}:${mesh.visible}`);
  });
  parts.sort();
  return parts.join("|");
}

/**
 * Sahnedeki Mesh / InstancedMesh öğelerinden materyal başına `BufferGeometry` listesi.
 * Geometri veya materyal referansı sahne ile paylaşılır; dispose burada yapılmaz.
 */
export class MaterialGeometryCache {
  private _modes = MaterialGeometryInvalidationMode.StructureSignature;
  private _stale = true;
  private _lastSignature = "";
  private _buckets = new Map<Material, BufferGeometry[]>();

  setModes(modes: MaterialGeometryInvalidationMode): void {
    this._modes = modes;
    this._stale = true;
  }

  getModes(): MaterialGeometryInvalidationMode {
    return this._modes;
  }

  /** Son başarılı `sync` sonrası yapı imzası (reparent grubu yenileme için). */
  getLastStructureSignature(): string {
    return this._lastSignature;
  }

  /** Sonraki `sync` tam tarama yapar. */
  markStale(): void {
    this._stale = true;
  }

  /** `markStale` ile aynı; plan adıyla uyumlu. */
  invalidate(): void {
    this.markStale();
  }

  getBuckets(): ReadonlyMap<Material, readonly BufferGeometry[]> {
    return this._buckets;
  }

  forEachMaterialGeometry(
    callback: (material: Material, geometries: readonly BufferGeometry[]) => void,
  ): void {
    for (const [mat, geoms] of this._buckets) {
      callback(mat, geoms);
    }
  }

  sync(scene: Scene | undefined): void {
    if (!scene) return;

    const sigOn =
      (this._modes & MaterialGeometryInvalidationMode.StructureSignature) !== 0;

    if (!this._stale && sigOn) {
      const sig = collectStructureSignatureOnly(scene);
      if (sig === this._lastSignature) {
        return;
      }
      this._lastSignature = sig;
      const { buckets } = collectStructureAndBuckets(scene);
      this._buckets = buckets;
      return;
    }

    if (!this._stale && !sigOn) {
      return;
    }

    const { signature, buckets } = collectStructureAndBuckets(scene);
    this._lastSignature = signature;
    this._buckets = buckets;
    this._stale = false;
  }
}
